/**
 * Which deployment environment is this?
 *
 * Vercel sets `VERCEL_ENV` to "production" | "preview" | "development", and leaves
 * it undefined everywhere else (local dev, CI, self-hosted).
 *
 * Note the deliberate asymmetry below. Getting this wrong in one direction leaks a
 * preview deployment into search results; getting it wrong in the other direction
 * de-indexes the production marketing site. For a company selling AI visibility,
 * the second is far worse — so `isNonProductionDeployment()` only returns true when
 * it can positively identify a non-production Vercel environment, and a missing or
 * unrecognised variable is always treated as "do not add noindex".
 */

function deploymentEnv(): string | undefined {
	return process.env.VERCEL_ENV ?? process.env.NEXT_PUBLIC_VERCEL_ENV;
}

/** True only on a Vercel production deployment. */
export function isProductionDeployment(): boolean {
	return deploymentEnv() === "production";
}

/**
 * True only when this is positively a non-production Vercel deployment — preview,
 * or any custom environment. False when the variable is absent, so a misconfigured
 * or non-Vercel environment can never mark production as noindex.
 */
export function isNonProductionDeployment(): boolean {
	const env = deploymentEnv();
	return env !== undefined && env !== "production";
}
